import React, { useMemo, useCallback } from 'react';
import { Box, Typography, Grid, Button, Alert, Chip, CircularProgress } from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import moment from 'moment-timezone';
import { ShiftRequest } from 'types/shift-requests';
import { useQuery } from 'react-query';
import { getDays } from 'core/API/organizationSettings';
import { getTimeOffReasons } from 'core/API/scheduler';
import { getShiftRequests } from 'core/API/shift-requests';
import { apiService } from 'core/services/apiService';
import { UserAvailability } from 'core/interfaces/userInterface';
import { ReadonlyFormField } from 'core/components/ReadonlyFormField';
import AvailabilityComparison from '../AvailabilityComparison/AvailabilityComparison';
import {
	buildAllWeekDays,
	collectRelatedAvailabilityRequests,
} from '../../utils/availabilityDisplay';
import { buildShiftsMap, lookupShift } from '../../utils/shiftRequestScheduleDates';
import { formatTimeOffPeriod, isTimedTimeOff } from '../../utils/timeOffRequestForm';

interface RequestDetailViewProps {
	request: ShiftRequest;
	onApprove: () => void;
	onDeny: () => void;
	onClose: () => void;
	canManageRequests?: boolean;
	allShifts?: any[]; // All shifts from schedule page
}

const actionButtonSx = {
	textTransform: 'none' as const,
	fontWeight: 600,
	px: { xs: 2.5, sm: 3 },
	py: { xs: 1.25, sm: 1 },
	minHeight: { xs: 44, sm: 36 },
	fontSize: { xs: '0.95rem', sm: '0.875rem' },
	flex: { xs: 1, sm: 'none' },
	gap: 0.5,
};

const RequestDetailView: React.FC<RequestDetailViewProps> = ({
	request,
	onApprove,
	onDeny,
	onClose,
	canManageRequests = false,
	allShifts = [],
}) => {
	const requestType = request.type?.label?.toLowerCase() || '';
	
	const requestShiftsMap = useMemo(
		() => buildShiftsMap(allShifts || []),
		[allShifts]
	);

	// Find shift details from allShifts, including off-week pickup/cover shifts
	const findShiftDetails = useCallback((shiftRequest: any): any => {
		if (!shiftRequest) return null;
		return lookupShift(shiftRequest, requestShiftsMap);
	}, [requestShiftsMap]);

	// Use full shift data if found, otherwise use request data
	const enrichedShiftRequest = useMemo(() => {
		return findShiftDetails(request.shiftRequest);
	}, [request.shiftRequest, findShiftDetails]);

	const enrichedShiftAssigned = useMemo(() => {
		return findShiftDetails(request.shiftAssigned);
	}, [request.shiftAssigned, findShiftDetails]);
	
	// Fetch days for availability display
	const { data: daysData = [] } = useQuery(['days'], () => getDays());
	const allDays = useMemo(() => buildAllWeekDays(daysData), [daysData]);

	// Fetch time-off reasons
	const { data: timeOffReasonsData = [] } = useQuery(['time-off-reasons'], () => getTimeOffReasons());

	// Fetch other users' time-off requests for the same period (only for time-off requests)
	const isTimeOffRequest = requestType.includes('time off') || requestType.includes('time-off');
	const isAvailabilityRequest = requestType.includes('availability');
	const workplaceId = request.workplace?.id;
	const employeeId = request.employeeRequest?.id || request.createdBy?.id;

	const { data: employeeUser } = useQuery(
		['user-availability', employeeId],
		async () => {
			const response = await apiService.get(`/users/${employeeId}`);
			return apiService.responseHandler(response);
		},
		{
			enabled: isAvailabilityRequest && !!employeeId,
		}
	);
	const currentAvailability: UserAvailability[] = employeeUser?.userAvailability || [];
	
	const { data: allShiftRequests = [], isLoading: isLoadingOtherTimeOffs } = useQuery(
		['shift-requests', workplaceId],
		() => getShiftRequests({ workplace_id: workplaceId }),
		{
			enabled:
				!!workplaceId &&
				((isTimeOffRequest && !!request.startAt && !!request.endAt) || isAvailabilityRequest),
		}
	);

	// Filter for other users' overlapping time-off requests
	const otherUsersTimeOff = useMemo(() => {
		if (!isTimeOffRequest || !request.startAt || !request.endAt) {
			return [];
		}

		const requests = Array.isArray(allShiftRequests) 
			? allShiftRequests 
			: (allShiftRequests.member || allShiftRequests['hydra:member'] || []);
		const selectedStart = moment(request.startAt);
		const selectedEnd = moment(request.endAt);
		const currentRequestId = request.id;

		return requests.filter((req: ShiftRequest) => {
			// Check if it's a time-off request (not the current request)
			if (req.id === currentRequestId) return false;
			
			const isTimeOff = req.type?.label?.toLowerCase().includes('time off') ||
				req.type?.label?.toLowerCase().includes('time-off');
			
			if (!isTimeOff || !req.startAt || !req.endAt) return false;

			// Check if approved or pending (not denied)
			const isApprovedOrPending = req.employeeRequestConfirmed !== false;
			if (!isApprovedOrPending) return false;

			// Check for date overlap
			const requestStart = moment(req.startAt);
			const requestEnd = moment(req.endAt);

			return requestStart.isSameOrBefore(selectedEnd) && requestEnd.isSameOrAfter(selectedStart);
		});
	}, [allShiftRequests, request.startAt, request.endAt, request.id, isTimeOffRequest]);

	const relatedAvailabilityRequests = useMemo(() => {
		if (!isAvailabilityRequest) return [request];
		return collectRelatedAvailabilityRequests(allShiftRequests, request);
	}, [allShiftRequests, isAvailabilityRequest, request]);

	// Find time-off reason name
	const getTimeOffReasonName = (reasonData: any): string => {
		if (!reasonData) return 'N/A';
		
		// If it's an object with @id, extract the @id
		let reasonIri: string | null = null;
		if (typeof reasonData === 'object' && reasonData['@id']) {
			reasonIri = reasonData['@id'];
		} else if (typeof reasonData === 'string') {
			reasonIri = reasonData;
		}
		
		if (!reasonIri) return 'N/A';
		
		const reason = timeOffReasonsData.find((r: any) => r['@id'] === reasonIri);
		return reason?.name || reasonIri;
	};

	// Format shift display
	const formatShiftDisplay = (shift: any) => {
		if (!shift) return 'N/A';
		const startRaw = shift.startAt || shift.start_at || shift.start;
		const endRaw = shift.endAt || shift.end_at || shift.end;
		const startTime = startRaw ? moment(startRaw).format('ddd, MMM D - h:mm A') : '';
		const endTime = endRaw ? moment(endRaw).format('h:mm A') : '';
		const jobCode = shift.jobCode?.label ? ` | ${shift.jobCode.label}` : '';

		if (startTime && endTime) {
			return `${startTime} - ${endTime}${jobCode}`;
		}
		return 'N/A';
	};

	// Get employee name
	const getEmployeeName = (): string => {
		if (request.employeeRequest) {
			return `${request.employeeRequest.firstName} ${request.employeeRequest.lastName || ''}`.trim();
		}
		if (request.createdBy) {
			return `${request.createdBy.firstName || ''} ${request.createdBy.lastName || ''}`.trim();
		}
		return 'Unknown Employee';
	};

	// Get request details based on type
	const renderRequestDetails = () => {
		if (requestType.includes('time off') || requestType.includes('time-off')) {
			const timed = isTimedTimeOff(request.startAt, request.endAt);
			return (
				<>
					<Grid item xs={12} sm={6}>
						<ReadonlyFormField
							label="Start Date"
							value={request.startAt ? moment(request.startAt).format('YYYY-MM-DD') : ''}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<ReadonlyFormField
							label="End Date"
							value={request.endAt ? moment(request.endAt).format('YYYY-MM-DD') : ''}
						/>
					</Grid>
					{timed && (
						<>
							<Grid item xs={12} sm={6}>
								<ReadonlyFormField
									label="Start Time"
									value={request.startAt ? moment(request.startAt).format('h:mm A') : ''}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<ReadonlyFormField
									label="End Time"
									value={request.endAt ? moment(request.endAt).format('h:mm A') : ''}
								/>
							</Grid>
						</>
					)}
					<Grid item xs={12}>
						<ReadonlyFormField
							label="Reason"
							value={getTimeOffReasonName((request as any).timeOffReasons || (request as any).reason)}
						/>
					</Grid>
					{(request).reasonDescription && (
						<Grid item xs={12}>
							<ReadonlyFormField
								label="Reason Description"
								value={(request).reasonDescription}
								multiline
								minRows={2}
							/>
						</Grid>
					)}
					{(request).reasonNote && (
						<Grid item xs={12}>
							<ReadonlyFormField
								label="Reason Note"
								value={(request).reasonNote}
								multiline
								minRows={2}
							/>
						</Grid>
					)}
					{otherUsersTimeOff.length > 0 && (
						<Grid item xs={12}>
							<Alert severity="info" sx={{ py: 0.5, mt: 2 }}>
								<Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
									Other users on time off during this period:
								</Typography>
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
									{otherUsersTimeOff.map((req: ShiftRequest) => (
										<Chip
											key={req.id}
											label={`${req.employeeRequest?.firstName || ''} ${req.employeeRequest?.lastName || ''} (${formatTimeOffPeriod(req.startAt, req.endAt)})`}
											size="small"
											color="primary"
											variant="outlined"
										/>
									))}
								</Box>
							</Alert>
						</Grid>
					)}
				</>
			);
		}

		if (requestType.includes('availability')) {
			return (
				<Grid item xs={12}>
					<AvailabilityComparison
						days={allDays}
						currentAvailability={currentAvailability}
						relatedRequests={relatedAvailabilityRequests}
					/>
				</Grid>
			);
		}

		if (requestType.includes('cover')) {
			return (
				<>
					<Grid item xs={12}>
						<ReadonlyFormField
							label="Shift to Cover"
							value={formatShiftDisplay(enrichedShiftRequest)}
						/>
					</Grid>
					{((request as any).timeOffReasons || request.reason) && (
						<Grid item xs={12}>
							<ReadonlyFormField
								label="Reason"
								value={getTimeOffReasonName((request as any).timeOffReasons || request.reason)}
							/>
						</Grid>
					)}
					{(request as any).reasonDescription && (
						<Grid item xs={12}>
							<ReadonlyFormField
								label="Reason Description"
								value={(request as any).reasonDescription}
								multiline
								minRows={2}
							/>
						</Grid>
					)}
					{(request as any).reasonNote && (
						<Grid item xs={12}>
							<ReadonlyFormField
								label="Reason Note"
								value={(request as any).reasonNote}
								multiline
								minRows={2}
							/>
						</Grid>
					)}
				</>
			);
		}

		if (requestType.includes('trade')) {
			return (
				<>
					<Grid item xs={12}>
						<ReadonlyFormField
							label="My Shift"
							value={formatShiftDisplay(enrichedShiftRequest)}
						/>
					</Grid>
					<Grid item xs={12}>
						<ReadonlyFormField
							label="Trade With Employee"
							value={`${(request.employeeAssigned as any).firstName || ''} ${(request.employeeAssigned as any).lastName || ''}`.trim() || 'Unknown'}
						/>
					</Grid>
					{enrichedShiftAssigned && (
						<Grid item xs={12}>
							<ReadonlyFormField
								label="Their Shift"
								value={formatShiftDisplay(enrichedShiftAssigned)}
							/>
						</Grid>
					)}
				</>
			);
		}

		if (requestType.includes('pick') || requestType.includes('pickup')) {
			return (
				<>
					<Grid item xs={12}>
						<ReadonlyFormField
							label="Shift"
							value={formatShiftDisplay(enrichedShiftRequest)}
						/>
					</Grid>
					{request.reason && (
						<Grid item xs={12}>
							<ReadonlyFormField
								label="Reason"
								value={request.reason}
								multiline
								minRows={2}
							/>
						</Grid>
					)}
				</>
			);
		}

		return null;
	};

	return (
		<Box sx={{ py: 2, display: 'flex', flexDirection: 'column', minHeight: { xs: '70vh', sm: 'auto' } }}>
			<Box px={3} sx={{ flex: 1 }}>
				{/* Header */}
				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					mb={3}
					gap={2}
					flexWrap="wrap"
				>
					<Button
						onClick={onClose}
						sx={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: '#fff',
							textTransform: 'none',
							fontWeight: 500,
							px: 3,
							py: 0.75,
							'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
						}}
					>
						Back
					</Button>
					<Typography
						variant="h5"
						id="employee-request-detail-title"
						sx={{
							fontWeight: 600,
							flex: 1,
							textAlign: { xs: 'left', sm: 'center' },
							minWidth: 0,
						}}
					>
						{request.type?.label || 'Unknown'} Request
					</Typography>
					{canManageRequests ? (
						<Box
							sx={{
								display: { xs: 'none', sm: 'flex' },
								flexDirection: 'column',
								alignItems: 'flex-end',
								gap: 1,
							}}
						>
							<Box sx={{ display: 'flex', gap: 2 }}>
								<Button
									onClick={onDeny}
									disabled={isTimeOffRequest && isLoadingOtherTimeOffs}
									startIcon={<Close />}
									sx={{
										...actionButtonSx,
										backgroundColor: '#dc3545',
										color: '#fff',
										'&:hover': { backgroundColor: '#c82333' },
									}}
								>
									Deny
								</Button>
								<Button
									onClick={onApprove}
									disabled={isTimeOffRequest && isLoadingOtherTimeOffs}
									startIcon={<Check />}
									sx={{
										...actionButtonSx,
										backgroundColor: '#4caf50',
										color: '#fff',
										'&:hover': { backgroundColor: '#45a049' },
									}}
								>
									Approve
								</Button>
							</Box>
							{isTimeOffRequest && isLoadingOtherTimeOffs && (
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<CircularProgress size={14} />
									<Typography variant="caption" color="text.secondary">
										Checking for other users on time off...
									</Typography>
								</Box>
							)}
						</Box>
					) : (
						<Box sx={{ width: { xs: 0, sm: '240px' }, display: { xs: 'none', sm: 'block' } }} />
					)}
				</Box>

				{/* Request Details */}
				<Box sx={{ p: { xs: 1, sm: 4 }, pb: { xs: canManageRequests ? 10 : 4, sm: 4 } }}>
					<Typography variant="h6" gutterBottom>
						Request Details
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						Review request information
					</Typography>

					<Grid container spacing={3}>
						<Grid item xs={12}>
							<ReadonlyFormField label="Employee" value={getEmployeeName()} />
						</Grid>

						<Grid item xs={12} sm={6}>
							<ReadonlyFormField
								label="Request Date"
								value={
									request.created_at ? moment(request.created_at).format('MM/DD/YYYY h:mm A') : ''
								}
							/>
						</Grid>

						<Grid item xs={12} sm={6}>
							<ReadonlyFormField
								label="Request Type"
								value={request.type?.label || 'Unknown'}
							/>
						</Grid>

						{renderRequestDetails()}

						{request.note && (
							<Grid item xs={12}>
								<ReadonlyFormField
									label="Notes"
									value={request.note}
									multiline
									minRows={3}
								/>
							</Grid>
						)}
					</Grid>
				</Box>
			</Box>

			{canManageRequests && (
				<Box
					sx={{
						display: { xs: 'flex', sm: 'none' },
						flexDirection: 'column',
						gap: 1,
						position: 'sticky',
						bottom: 0,
						px: 2,
						py: 1.5,
						mt: 'auto',
						backgroundColor: '#fff',
						borderTop: '1px solid #e0e0e0',
						boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.06)',
						zIndex: 1,
					}}
				>
					{isTimeOffRequest && isLoadingOtherTimeOffs && (
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
							<CircularProgress size={14} />
							<Typography variant="caption" color="text.secondary">
								Checking for other users on time off...
							</Typography>
						</Box>
					)}
					<Box sx={{ display: 'flex', gap: 1.5 }}>
						<Button
							onClick={onDeny}
							disabled={isTimeOffRequest && isLoadingOtherTimeOffs}
							startIcon={<Close />}
							sx={{
								...actionButtonSx,
								backgroundColor: '#dc3545',
								color: '#fff',
								'&:hover': { backgroundColor: '#c82333' },
							}}
						>
							Deny
						</Button>
						<Button
							onClick={onApprove}
							disabled={isTimeOffRequest && isLoadingOtherTimeOffs}
							startIcon={<Check />}
							sx={{
								...actionButtonSx,
								backgroundColor: '#4caf50',
								color: '#fff',
								'&:hover': { backgroundColor: '#45a049' },
							}}
						>
							Approve
						</Button>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default RequestDetailView;
